import { useMemo } from "react";
import { AlertTriangle, Info, X } from "lucide-react";
import { DetailRow, ResultBadge, StatusIcon } from "./shared";
import type { BenchmarkCase, CaseRunResult, CaseStatus } from "../types";

type DiffSegment = { text: string; type: "equal" | "removed" | "added" };

function diffWords(a: string, b: string): { left: DiffSegment[]; right: DiffSegment[] } {
  const wordsA = a.split(/(\s+)/);
  const wordsB = b.split(/(\s+)/);

  const m = wordsA.length;
  const n = wordsB.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = wordsA[i - 1] === wordsB[j - 1]
        ? dp[i - 1][j - 1] + 1
        : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }

  const left: DiffSegment[] = [];
  const right: DiffSegment[] = [];
  let i = m, j = n;
  const leftStack: DiffSegment[] = [];
  const rightStack: DiffSegment[] = [];

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && wordsA[i - 1] === wordsB[j - 1]) {
      leftStack.push({ text: wordsA[i - 1], type: "equal" });
      rightStack.push({ text: wordsB[j - 1], type: "equal" });
      i--; j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      rightStack.push({ text: wordsB[j - 1], type: "added" });
      j--;
    } else {
      leftStack.push({ text: wordsA[i - 1], type: "removed" });
      i--;
    }
  }

  leftStack.reverse().forEach((s) => {
    const prev = left[left.length - 1];
    if (prev && prev.type === s.type) prev.text += s.text;
    else left.push({ ...s });
  });
  rightStack.reverse().forEach((s) => {
    const prev = right[right.length - 1];
    if (prev && prev.type === s.type) prev.text += s.text;
    else right.push({ ...s });
  });

  return { left, right };
}

function DiffText({ segments }: { segments: DiffSegment[] }) {
  return (
    <>
      {segments.map((seg, i) =>
        seg.type === "equal" ? (
          <span key={i}>{seg.text}</span>
        ) : (
          <mark key={i} className="bg-yellow-200 rounded-sm px-px">{seg.text}</mark>
        ),
      )}
    </>
  );
}

function ComparisonField({
  label,
  original,
  modified,
}: {
  label: string;
  original: string;
  modified: string;
}) {
  const hasChanged = original !== modified;
  const diff = useMemo(
    () => (hasChanged ? diffWords(original, modified) : null),
    [original, modified, hasChanged],
  );

  return (
    <div>
      <dt className="text-xs font-medium text-brand-muted uppercase tracking-wider mb-1">
        {label}
      </dt>
      {hasChanged && diff ? (
        <div className="grid grid-cols-2 gap-3">
          <dd className="text-sm text-brand-foreground rounded-md bg-red-50 border border-red-200 px-3 py-2">
            <span className="text-[10px] font-semibold uppercase text-red-500 block mb-1">Original</span>
            {original ? <DiffText segments={diff.left} /> : "None"}
          </dd>
          <dd className="text-sm text-brand-foreground rounded-md bg-green-50 border border-green-200 px-3 py-2">
            <span className="text-[10px] font-semibold uppercase text-green-600 block mb-1">Modified</span>
            {modified ? <DiffText segments={diff.right} /> : "None"}
          </dd>
        </div>
      ) : (
        <dd className="text-sm text-brand-foreground">{original || "None"}</dd>
      )}
    </div>
  );
}

export function CaseDetailModal({
  benchmarkCase,
  result,
  status,
  messageCount,
  onClose,
}: {
  benchmarkCase: BenchmarkCase;
  result?: CaseRunResult;
  status: CaseStatus;
  messageCount: number;
  onClose: () => void;
}) {
  const { originalCase, modifiedCase } = benchmarkCase;
  const hasAnyChange =
    originalCase.situation !== modifiedCase.situation ||
    originalCase.segment !== modifiedCase.segment ||
    originalCase.situationSummary !== modifiedCase.situationSummary ||
    originalCase.expectedSuggestion !== modifiedCase.expectedSuggestion ||
    originalCase.personaDetail !== modifiedCase.personaDetail;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-brand-card border border-brand-border rounded-lg p-6 max-w-4xl w-full mx-4 shadow-xl max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-brand-foreground">
            {benchmarkCase.id < 0 ? "Custom Case" : `Case #${benchmarkCase.id}`} &mdash; {modifiedCase.expectedSuggestion || "No expected product"}
          </h2>
          <button
            onClick={onClose}
            className="text-brand-muted hover:text-brand-foreground transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {benchmarkCase.comments && (
          <div className="mb-5 flex gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span className="whitespace-pre-line">{benchmarkCase.comments}</span>
          </div>
        )}

        {!hasAnyChange && benchmarkCase.id >= 0 && (
          <div className="mb-5 flex gap-2 rounded-md border border-brand-border bg-brand-primary/5 px-4 py-2.5 text-sm text-brand-muted">
            <Info className="w-4 h-4 shrink-0 mt-0.5" />
            No modifications — original and modified cases are identical.
          </div>
        )}

        <div className="space-y-4">
          <DetailRow label="Status">
            <div className="flex items-center gap-2">
              <StatusIcon status={status} />
              <span className="capitalize">{status}</span>
              {result && (status === "complete" || status === "failed" || status === "abandoned") && (
                <ResultBadge result={result} />
              )}
            </div>
          </DetailRow>
          <DetailRow label="Total Messages">{messageCount}</DetailRow>
          <ComparisonField label="Situation Summary" original={originalCase.situationSummary} modified={modifiedCase.situationSummary} />
          <ComparisonField label="Persona" original={originalCase.personaDetail} modified={modifiedCase.personaDetail} />
          <ComparisonField label="Expected Situation" original={originalCase.situation} modified={modifiedCase.situation} />
          <ComparisonField label="Expected Segment" original={originalCase.segment} modified={modifiedCase.segment} />
          <ComparisonField label="Expected Suggestion" original={originalCase.expectedSuggestion} modified={modifiedCase.expectedSuggestion} />
          <DetailRow label="Actual Suggestion">
            {result
              ? (result.actualSuggestions.length ? result.actualSuggestions.join(", ") : "None")
              : "\u2014"}
          </DetailRow>
        </div>
      </div>
    </div>
  );
}
